import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Tag as TagIcon, X } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { supabase } from '../../lib/supabase';
import { projectService } from '../../services/api';

// Interface que representa a mensagem do chat
interface Message {
  id: string;
  content: string;
  sender: string;
  timestamp: string;
  type: 'text' | 'image' | 'file';
  projectId: string;
}

// Interface para os membros da equipe
interface TeamMember {
  id: string;
  name: string;
  role?: string;
  auth_id?: string;
  email?: string;
}

interface MessageToTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message;
  onCreateTask: (task: {
    title: string;
    description: string;
    dueDate: string;
    priority: 'low' | 'medium' | 'high';
    assignee: string;
  }) => void;
}

interface TaskFormData {
  title: string;
  description: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  assignee: string;
}

const MessageToTaskModal: React.FC<MessageToTaskModalProps> = ({
  isOpen,
  onClose,
  message,
  onCreateTask
}) => {
  const { organization } = useOrganization();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<TaskFormData>({
    title: message.content,
    description: '',
    dueDate: new Date().toISOString().split('T')[0], // Today's date as default
    priority: 'medium',
    assignee: ''
  });

  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!organization?.id || !message.projectId) {
        console.log('Faltam dados para buscar membros:', { 
          orgId: organization?.id, 
          projectId: message.projectId 
        });
        return;
      }

      try {
        setLoading(true);
        console.log('Buscando membros do projeto:', message.projectId);
        
        // Primeiro tenta buscar membros do projeto específico
        const projectMembers = await projectService.getProjectMembers(
          message.projectId, 
          organization.id
        );
        
        if (projectMembers && projectMembers.length > 0) {
          setTeamMembers(projectMembers);
          console.log('Membros do projeto encontrados:', projectMembers);
        } else {
          // Backup: se não houver membros específicos do projeto, busca membros da organização
          console.log('Nenhum membro do projeto encontrado, buscando membros da organização');
          
          const { data, error } = await supabase
            .from('user_profile')
            .select(`
              id,
              name,
              email,
              auth_id,
              organization_id
            `)
            .eq('organization_id', organization.id)
            .not('auth_id', 'is', null)
            .eq('archived', false)
            .order('name');
            
          if (error) {
            console.error('Erro ao buscar membros da organização:', error);
            return;
          }
          
          if (data && data.length > 0) {
            const formattedMembers = data.map(member => ({
              id: member.auth_id || member.id.toString(),
              name: member.name || member.email || 'Usuário sem nome',
              role: 'Membro da Organização',
              email: member.email
            }));
            
            setTeamMembers(formattedMembers);
            console.log('Membros da organização encontrados:', formattedMembers);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar membros:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchProjectMembers();
    }
  }, [isOpen, organization?.id, message.projectId]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreateTask(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">Criar Tarefa da Mensagem</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
                  rows={3}
                  placeholder="Adicione detalhes ou contexto para esta tarefa"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Entrega
                  </label>
                  <div className="relative">
                    <Calendar className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridade
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => {
                      const newPriority = e.target.value as 'low' | 'medium' | 'high';
                      setFormData(prev => ({ ...prev, priority: newPriority }));
                    }}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável
                </label>
                <select
                  value={formData.assignee}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignee: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
                >
                  <option value="">Selecionar responsável</option>
                  {loading ? (
                    <option value="" disabled>Carregando membros...</option>
                  ) : teamMembers.length > 0 ? (
                    teamMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name} {member.role ? `(${member.role})` : ''}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Nenhum membro encontrado</option>
                  )}
                </select>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Criar Tarefa
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageToTaskModal;